import { useQuery } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";

import Sentry from "@similarweb/sentry-logger";

import { apiFormat } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

/** Visit counts per channel, keyed by channel name (`MmxTrafficSource`). */
type ChannelVisits = Record<string, number | null>;

interface IPieChartPopResponse {
    Data: Record<string, Record<string, ChannelVisits>>;
    ComparedData: { Data: Record<string, Record<string, ChannelVisits>> };
}

export interface IChannelVisitsPop {
    current: ChannelVisits;
    compared: ChannelVisits;
}

interface IChannelsPopParams {
    keys: string;
    country: string;
    includeSubDomains: boolean;
    from: string;
    to: string;
    compareFrom: string;
    compareTo: string;
}

/**
 * The window the widget APIs treat as "current" for their month-over-month deltas — the last calendar
 * month of the selected range — plus the month before it. Requesting the same window for channels keeps
 * every Notable change on one baseline.
 */
export const getMonthOverMonthWindow = (rangeEnd: Dayjs) => {
    const previousMonth = rangeEnd.subtract(1, "month");

    return {
        from: rangeEnd.startOf("month").format(apiFormat),
        to: rangeEnd.endOf("month").format(apiFormat),
        compareFrom: previousMonth.startOf("month").format(apiFormat),
        compareTo: previousMonth.endOf("month").format(apiFormat),
    };
};

/**
 * Per-channel visits for the last month and the month before it.
 *
 * The overview bar chart already calls `PieChart` for the whole selected range; this is a separate
 * request because the ribbon needs a single-month baseline the bar chart's range cannot provide.
 */
export const useTrafficChannelsPopData = (
    params: IChannelsPopParams,
    controller: string,
    { enabled = true }: { enabled?: boolean } = {},
) =>
    useQuery<IChannelVisitsPop | null>({
        queryKey: ["websitePerformanceChannelsPop", params, controller],
        queryFn: async () => {
            try {
                const response = await DefaultFetchService.getInstance().get<IPieChartPopResponse>(
                    `widgetApi/${controller}/PieChartPop`,
                    {
                        keys: params.keys,
                        country: params.country,
                        includeSubDomains: params.includeSubDomains,
                        isWindow: false,
                        timeGranularity: "Monthly",
                        from: params.from,
                        to: params.to,
                        compareFrom: params.compareFrom,
                        compareTo: params.compareTo,
                    },
                );

                const current = response.Data?.[devicesTypes.TOTAL]?.[params.keys];
                const compared = response.ComparedData?.Data?.[devicesTypes.TOTAL]?.[params.keys];

                return current && compared ? { current, compared } : null;
            } catch (error) {
                Sentry.errorTracker({
                    e: error instanceof Error ? error : new Error(String(error)),
                    message: "Insight Ribbon failed to load channels",
                    level: "error",
                    params: { extra: { key: params.keys } },
                });
                throw error;
            }
        },
        enabled: enabled && !!params.keys,
    });
